/**
 * Full-page LeadFormView — Generate New Leads.
 * Sections: A Account · B PIC · C Lead Source · D Type & Category · E Profile
 *           · F Needs & Expectations · G Training · H Price · I Competitor · J Contacts
 * Supports BOTH "create" and "edit" modes.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Plus, RefreshCw, Trash2,
  Users as UsersIcon,
} from "lucide-react";
import { API } from "../../lib/api";

const EMPTY_PIC = { nama: "", jabatan: "", telepon: "" };
const EMPTY_CONTACT = { nama: "", jabatan: "", telepon: "" };
const REQUIRED_TOP = ["nama_account", "telepon_account", "category"];

const INITIAL = {
  nama_account: "", telepon_account: "", npsn: "", email_account: "",
  alamat_account: "", nama_yayasan: "",
  pic_utama: { ...EMPTY_PIC },
  pic_tambahan: [],
  decision_makers: [], decision_maker_other: "",
  lead_source: "", status_lead: "New Lead",
  transaction_type: "", category: "", category_specify: "",
  jumlah_student: "", jumlah_guru: "", jumlah_guru_inggris: "",
  tuition_fee: "", english_exposure: "",
  masalah_utama: [], masalah_utama_other: "",
  objectives: [], objectives_other: "",
  target_implementasi: "", target_implementasi_other: "",
  sifat_pelatihan: "", sifat_pelatihan_other: "",
  course_package: "", package_price_per_student: "", course_length: "",
  existing_program: "", competitor: "", competitor_other: "",
  contacts: [],
};

function CheckGroup({ label, options, values, onChange, testid }) {
  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };
  return (
    <div className="form-field" data-testid={testid}>
      <label>{label}</label>
      <div style={{display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4}}>
        {options.map((o) => (
          <label key={o} className={`chip-check ${values.includes(o) ? "on" : ""}`}>
            <input type="checkbox" checked={values.includes(o)} onChange={() => toggle(o)} />
            <span>{o}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function LeadFormView({ mode = "create", leadId = null, onSuccess, onCancel }) {
  const isEdit = mode === "edit";
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadMeta = useCallback(async () => {
    const r = await axios.get(`${API}/leads/meta`);
    setMeta(r.data);
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  useEffect(() => {
    if (!isEdit || !leadId) { setForm(INITIAL); return; }
    axios.get(`${API}/leads/${leadId}`).then((r) => {
      const d = r.data;
      setForm({
        ...INITIAL, ...d,
        pic_utama: d.pic_utama || { ...EMPTY_PIC },
        pic_tambahan: d.pic_tambahan || [],
        contacts: d.contacts || [],
        decision_makers: d.decision_makers || [],
        masalah_utama: d.masalah_utama || [],
        objectives: d.objectives || [],
      });
    }).catch((e) => setError(e?.response?.data?.detail || "Gagal load lead"));
  }, [isEdit, leadId]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updPic = (k, v) => upd("pic_utama", { ...form.pic_utama, [k]: v });
  const setListItem = (key) => (i, patch) => {
    upd(key, form[key].map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };
  const addPic = () => {
    if (form.pic_tambahan.length >= 4) return;
    upd("pic_tambahan", [...form.pic_tambahan, { ...EMPTY_PIC }]);
  };
  const addContact = () => {
    if (form.contacts.length >= 10) return;
    upd("contacts", [...form.contacts, { ...EMPTY_CONTACT }]);
  };
  const removeAt = (key, i) => upd(key, form[key].filter((_, idx) => idx !== i));

  const estimatedValue = useMemo(() => {
    const n = Number(form.jumlah_student) || 0;
    let p = Number(form.package_price_per_student) || 0;
    if (!p && form.course_package && meta?.default_prices?.[form.course_package]) {
      p = meta.default_prices[form.course_package];
    }
    return n && p ? n * p : 0;
  }, [form.jumlah_student, form.package_price_per_student, form.course_package, meta]);

  const validate = () => {
    const missing = REQUIRED_TOP.filter((k) => !form[k]);
    if (missing.length) { setError(`Field wajib kosong: ${missing.join(", ")}`); window.scrollTo({top: 0, behavior: "smooth"}); return false; }
    if (!form.pic_utama.nama) { setError("PIC Utama wajib diisi"); window.scrollTo({top: 0, behavior: "smooth"}); return false; }
    setError(null);
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const payload = {
      ...form,
      jumlah_student: form.jumlah_student ? Number(form.jumlah_student) : null,
      jumlah_guru: form.jumlah_guru ? Number(form.jumlah_guru) : null,
      jumlah_guru_inggris: form.jumlah_guru_inggris ? Number(form.jumlah_guru_inggris) : null,
      tuition_fee: form.tuition_fee ? Number(form.tuition_fee) : null,
      package_price_per_student: form.package_price_per_student ? Number(form.package_price_per_student) : null,
      pic_tambahan: form.pic_tambahan.filter((p) => p.nama),
      contacts: form.contacts.filter((c) => c.nama),
    };
    try {
      if (isEdit && leadId) {
        await axios.patch(`${API}/leads/${leadId}`, payload);
      } else {
        await axios.post(`${API}/leads`, payload);
      }
      onSuccess && onSuccess();
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSubmitting(false); }
  };

  if (!meta) return <div className="empty">Memuat form...</div>;

  return (
    <div className="form-page" data-testid="lead-form-page">
      <div className="form-page-head">
        <button className="btn btn-ghost" onClick={onCancel} data-testid="lead-back"><ArrowLeft size={14}/> Kembali</button>
        <div style={{flex: 1}}>
          <h2 style={{margin: 0, color: "var(--navy)", fontSize: 22}}>
            <UsersIcon size={20} style={{verticalAlign: "-3px", marginRight: 8}}/>
            {isEdit ? "Edit Lead" : "Generate New Lead"}
          </h2>
          <p style={{margin: "4px 0 0 0", color: "var(--muted)", fontSize: 13}}>
            Pastikan PIC Utama & Kategori wajib. Setiap perubahan otomatis tercatat di status history.
          </p>
        </div>
      </div>

      {error && <div className="alert err" style={{margin: "16px 0"}}><AlertTriangle size={14}/> {error}</div>}

      <div className="form-sections">
        <section className="form-section">
          <h3 className="form-section-title">A. Informasi Akun & Institusi</h3>
          <div className="form-grid">
            <div className="form-field"><label>Nama Account / Institusi<span className="req">*</span></label>
              <input className="form-input" value={form.nama_account} onChange={(e) => upd("nama_account", e.target.value)} required data-testid="f-nama_account" /></div>
            <div className="form-field"><label>Nomor Telepon<span className="req">*</span></label>
              <input className="form-input" value={form.telepon_account} onChange={(e) => upd("telepon_account", e.target.value)} required data-testid="f-telepon_account" /></div>
            <div className="form-field"><label>NPSN
              <button type="button"
                style={{marginLeft: 10, padding: "2px 10px", fontSize: 11, borderRadius: 12,
                        border: "1px solid var(--accent)", background: "rgba(46,134,222,0.08)",
                        color: "var(--accent)", cursor: "pointer", fontWeight: 600}}
                onClick={async () => {
                  const n = (form.npsn || "").trim();
                  if (!n) { setError("Isi NPSN dulu"); window.scrollTo({top: 0, behavior: "smooth"}); return; }
                  try {
                    const r = await axios.get(`${API}/schools/lookup/${encodeURIComponent(n)}`);
                    if (r.data?.ok) {
                      setForm((f) => ({...f, nama_account: f.nama_account || r.data.nama_sekolah,
                        alamat_account: f.alamat_account || r.data.alamat || ""}));
                      setError(null);
                    } else { setError(r.data?.reason || "NPSN tidak ditemukan"); window.scrollTo({top: 0, behavior: "smooth"}); }
                  } catch { setError("Gagal lookup NPSN"); }
                }}
                data-testid="lead-lookup-npsn"
                title="Auto-fill dari NPSN (kemendikdasmen)">🔍 Auto-fill</button>
              </label>
              <input className="form-input" value={form.npsn} onChange={(e) => upd("npsn", e.target.value)} /></div>
            <div className="form-field"><label>Email Resmi</label>
              <input className="form-input" type="email" value={form.email_account} onChange={(e) => upd("email_account", e.target.value)} /></div>
            <div className="form-field"><label>Alamat</label>
              <input className="form-input" value={form.alamat_account} onChange={(e) => upd("alamat_account", e.target.value)} /></div>
            <div className="form-field"><label>Nama Yayasan</label>
              <input className="form-input" value={form.nama_yayasan} onChange={(e) => upd("nama_yayasan", e.target.value)} /></div>
          </div>
        </section>

        <section className="form-section">
          <h3 className="form-section-title">B. Kontak & PIC</h3>
          <div className="form-grid">
            <div className="form-field"><label>Nama PIC Utama<span className="req">*</span></label>
              <input className="form-input" value={form.pic_utama.nama} onChange={(e) => updPic("nama", e.target.value)} required data-testid="f-pic-utama-nama" /></div>
            <div className="form-field"><label>Jabatan</label>
              <input className="form-input" value={form.pic_utama.jabatan} onChange={(e) => updPic("jabatan", e.target.value)} /></div>
            <div className="form-field"><label>Nomor Telepon</label>
              <input className="form-input" value={form.pic_utama.telepon} onChange={(e) => updPic("telepon", e.target.value)} /></div>
          </div>
          <div style={{marginTop: 18, paddingTop: 14, borderTop: "1px dashed var(--border-2)"}}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10}}>
              <span style={{fontSize: 13, fontWeight: 600, color: "var(--navy)"}}>PIC Tambahan ({form.pic_tambahan.length}/4)</span>
              <button type="button" className="btn btn-secondary" onClick={addPic} disabled={form.pic_tambahan.length >= 4} data-testid="add-pic">
                <Plus size={12}/> Tambah PIC
              </button>
            </div>
            {form.pic_tambahan.map((p, i) => (
              <div key={i} className="form-grid" style={{marginBottom: 12, paddingBottom: 12, borderBottom: "1px dashed var(--border-2)"}}>
                <div className="form-field"><label>Nama PIC {i + 2}</label>
                  <input className="form-input" value={p.nama} onChange={(e) => setListItem("pic_tambahan")(i, { nama: e.target.value })} /></div>
                <div className="form-field"><label>Jabatan</label>
                  <input className="form-input" value={p.jabatan} onChange={(e) => setListItem("pic_tambahan")(i, { jabatan: e.target.value })} /></div>
                <div className="form-field"><label>Telepon</label>
                  <input className="form-input" value={p.telepon} onChange={(e) => setListItem("pic_tambahan")(i, { telepon: e.target.value })} /></div>
                <div style={{display: "flex", alignItems: "flex-end"}}>
                  <button type="button" className="icon-btn" onClick={() => removeAt("pic_tambahan", i)} style={{color: "var(--bad)"}}><Trash2 size={14}/></button>
                </div>
              </div>
            ))}
          </div>
          <div style={{marginTop: 16}}>
            <CheckGroup label="Decision Maker" options={meta.decision_makers} values={form.decision_makers}
              onChange={(v) => upd("decision_makers", v)} testid="f-decision_makers" />
          </div>
        </section>

        <section className="form-section">
          <h3 className="form-section-title">C. Informasi Lead & Sales</h3>
          <div className="form-grid">
            <div className="form-field"><label>Lead Source</label>
              <select className="form-input" value={form.lead_source} onChange={(e) => upd("lead_source", e.target.value)} data-testid="f-lead_source">
                <option value="">— Pilih —</option>
                {meta.lead_sources.map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
            <div className="form-field"><label>Status Lead</label>
              <input className="form-input" value={form.status_lead} disabled style={{background: "var(--bg-2)"}} /></div>
          </div>
        </section>

        <section className="form-section">
          <h3 className="form-section-title">D. Tipe & Kategori Institusi</h3>
          <div className="form-grid">
            <div className="form-field"><label>Transaction Type</label>
              <select className="form-input" value={form.transaction_type} onChange={(e) => upd("transaction_type", e.target.value)}>
                <option value="">— Pilih —</option>
                {meta.transaction_types.map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
            <div className="form-field"><label>Category<span className="req">*</span></label>
              <select className="form-input" value={form.category} onChange={(e) => upd("category", e.target.value)} required data-testid="f-category">
                <option value="">— Pilih —</option>
                {meta.categories.map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
            {form.category && form.category.includes("SMK") && (
              <div className="form-field"><label>Specify (SMK)</label>
                <input className="form-input" value={form.category_specify} onChange={(e) => upd("category_specify", e.target.value)} placeholder="Akuntansi, Komputer, dll" /></div>
            )}
          </div>
        </section>

        <section className="form-section">
          <h3 className="form-section-title">E. Profil Institusi</h3>
          <div className="form-grid">
            <div className="form-field"><label>Jumlah Student</label>
              <input className="form-input" type="number" min="0" value={form.jumlah_student} onChange={(e) => upd("jumlah_student", e.target.value)} data-testid="f-jumlah_student" /></div>
            <div className="form-field"><label>Jumlah Guru / Trainer</label>
              <input className="form-input" type="number" min="0" value={form.jumlah_guru} onChange={(e) => upd("jumlah_guru", e.target.value)} /></div>
            <div className="form-field"><label>Jumlah Guru Bahasa Inggris</label>
              <input className="form-input" type="number" min="0" value={form.jumlah_guru_inggris} onChange={(e) => upd("jumlah_guru_inggris", e.target.value)} /></div>
            <div className="form-field"><label>Tuition Fee (Rp)</label>
              <input className="form-input" type="number" min="0" value={form.tuition_fee} onChange={(e) => upd("tuition_fee", e.target.value)} /></div>
            <div className="form-field"><label>English Exposure</label>
              <select className="form-input" value={form.english_exposure} onChange={(e) => upd("english_exposure", e.target.value)}>
                <option value="">— Pilih —</option>
                {meta.english_exposure.map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
          </div>
        </section>

        <section className="form-section">
          <h3 className="form-section-title">F. Kebutuhan & Ekspektasi</h3>
          <CheckGroup label="Masalah Utama" options={meta.issues} values={form.masalah_utama}
            onChange={(v) => upd("masalah_utama", v)} testid="f-masalah_utama" />
          <CheckGroup label="Objectives / Expected Outcome" options={meta.objectives} values={form.objectives}
            onChange={(v) => upd("objectives", v)} />
          <div className="form-grid" style={{marginTop: 12}}>
            <div className="form-field"><label>Target Implementasi</label>
              <select className="form-input" value={form.target_implementasi} onChange={(e) => upd("target_implementasi", e.target.value)}>
                <option value="">— Pilih —</option>
                {meta.implementation_targets.map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
          </div>
        </section>

        <section className="form-section">
          <h3 className="form-section-title">G. Sifat & Paket Pelatihan</h3>
          <div className="form-grid">
            <div className="form-field"><label>Sifat Pelatihan</label>
              <select className="form-input" value={form.sifat_pelatihan} onChange={(e) => upd("sifat_pelatihan", e.target.value)}>
                <option value="">— Pilih —</option>
                {meta.training_natures.map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
            <div className="form-field"><label>Course Package</label>
              <select className="form-input" value={form.course_package} onChange={(e) => upd("course_package", e.target.value)}>
                <option value="">— Pilih —</option>
                {meta.course_packages.map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
          </div>
        </section>

        <section className="form-section">
          <h3 className="form-section-title">H. Harga & Durasi</h3>
          <div className="form-grid">
            <div className="form-field"><label>Package Price per Student (Rp)
              {form.course_package && meta.default_prices?.[form.course_package] && (
                <span style={{fontSize: 11, color: "var(--muted)", marginLeft: 8}}>
                  default: {meta.default_prices[form.course_package].toLocaleString("id-ID")}
                </span>
              )}
            </label>
              <input className="form-input" type="number" min="0" value={form.package_price_per_student}
                onChange={(e) => upd("package_price_per_student", e.target.value)}
                placeholder={meta.default_prices?.[form.course_package] || "Custom"} /></div>
            <div className="form-field"><label>Course Length per Level</label>
              <input className="form-input" value={form.course_length} onChange={(e) => upd("course_length", e.target.value)} placeholder="e.g. 1 academic year (28 effective weeks)" /></div>
            <div className="form-field"><label>Estimated Deal Value (auto)</label>
              <input className="form-input" disabled style={{background: "var(--bg-2)", fontWeight: 700, color: "var(--good)"}}
                value={estimatedValue ? `Rp ${estimatedValue.toLocaleString("id-ID")}` : "—"} data-testid="f-estimated_value" /></div>
          </div>
        </section>

        <section className="form-section">
          <h3 className="form-section-title">I. Kompetitor & Existing Condition</h3>
          <div className="form-grid">
            <div className="form-field"><label>Existing Program</label>
              <input className="form-input" value={form.existing_program} onChange={(e) => upd("existing_program", e.target.value)} /></div>
            <div className="form-field"><label>Competitor</label>
              <select className="form-input" value={form.competitor} onChange={(e) => upd("competitor", e.target.value)}>
                <option value="">— Pilih —</option>
                {meta.competitors.map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
          </div>
        </section>

        <section className="form-section">
          <h3 className="form-section-title">J. Contact List</h3>
          <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10}}>
            <span style={{fontSize: 13, color: "var(--muted)"}}>{form.contacts.length}/10 kontak</span>
            <button type="button" className="btn btn-secondary" onClick={addContact} disabled={form.contacts.length >= 10} data-testid="add-contact">
              <Plus size={12}/> Tambah Kontak
            </button>
          </div>
          {form.contacts.map((c, i) => (
            <div key={i} className="form-grid" style={{marginBottom: 12, paddingBottom: 12, borderBottom: "1px dashed var(--border-2)"}}>
              <div className="form-field"><label>Nama Relasi {i + 1}</label>
                <input className="form-input" value={c.nama} onChange={(e) => setListItem("contacts")(i, { nama: e.target.value })} /></div>
              <div className="form-field"><label>Jabatan</label>
                <input className="form-input" value={c.jabatan} onChange={(e) => setListItem("contacts")(i, { jabatan: e.target.value })} /></div>
              <div className="form-field"><label>Telepon</label>
                <input className="form-input" value={c.telepon} onChange={(e) => setListItem("contacts")(i, { telepon: e.target.value })} /></div>
              <div style={{display: "flex", alignItems: "flex-end"}}>
                <button type="button" className="icon-btn" onClick={() => removeAt("contacts", i)} style={{color: "var(--bad)"}}><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </section>
      </div>

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onCancel} disabled={submitting} data-testid="lead-cancel">Batal</button>
        <button className="btn btn-primary" onClick={submit} disabled={submitting} data-testid="lead-submit">
          {submitting ? <><RefreshCw size={14} className="spin"/> Memproses...</> : <><CheckCircle2 size={14}/> {isEdit ? "Simpan Perubahan" : "Submit Lead"}</>}
        </button>
      </div>
    </div>
  );
}
